import React, { useState } from "react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import Button from "./library/Button";
import ApplicationRecord from "../apiResources/ApplicationRecord";

const MultiPanelForm = <Values extends Record<string, unknown>>(
  props: Props<Values>
) => {
  const [panel, setPanel] = useState<number>(0);
  const { children, initialValues, validationSchemas, onSubmit } = props;

  const activeForm = React.Children.toArray(children)[panel];

  const validation = validationSchemas[panel];

  const panelCount = React.Children.count(children);

  const isLastPanel = panel === panelCount - 1;

  const onPanelSubmit = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    if (isLastPanel) {
      const result = await onSubmit(values);

      if (result?.errors && Object.keys(result.errors).length) {
        Object.entries(result.errors).forEach(([key, error]) => {
          helpers.setFieldError(key, error.fullMessage);
        });

        const errorKeys = Object.keys(result.errors);

        const activePanel = validationSchemas
          .map((schema) => Object.keys(schema.fields))
          .findIndex((panelKeys) =>
            errorKeys.some((key) => panelKeys.includes(key))
          );

        if (activePanel !== -1) {
          setPanel(activePanel);
        }
      }
    } else {
      helpers.setSubmitting(false);
      // It's important we reset the touched state when the panel changes
      // otherwise, as the form has technically submitted, any field
      // changing will trigger re-validation on all the fields.
      Object.keys(values).forEach((field) =>
        helpers.setFieldTouched(field, false, false)
      );
      setPanel(panel + 1);
    }
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validation}
        onSubmit={onPanelSubmit}
      >
        <Form autoComplete="off">
          {activeForm}
          <Footer
            onPreviousClick={() => setPanel(panel - 1)}
            step={panel + 1}
            stepCount={panelCount}
          />
        </Form>
      </Formik>
    </div>
  );
};

type Props<Values extends Record<string, unknown>> = {
  children: React.ReactChild[];
  initialValues: Values;
  onSubmit: (values: Values) => Promise<ApplicationRecord>;
  validationSchemas: Yup.AnyObjectSchema[];
};

const Footer = (props: FooterProps) => {
  const { step, stepCount, onPreviousClick } = props;
  const { isSubmitting } = useFormikContext();

  let actionText = "Next";

  if (step === stepCount) {
    actionText = "Save";
  }

  if (isSubmitting) {
    actionText = "Saving...";
  }

  return (
    <FormFooter>
      {step === 1 ? (
        <ButtonPlaceholder />
      ) : (
        <Button
          type="button"
          name="Previous"
          tertiary
          onClick={onPreviousClick}
        />
      )}
      <StepIndicator>
        Step {step} of {stepCount}
      </StepIndicator>
      <Button type="submit" name={actionText} disabled={isSubmitting} primary />
    </FormFooter>
  );
};

type FooterProps = {
  step: number;
  stepCount: number;
  onPreviousClick: () => void;
};

const StepIndicator = styled.div`
  color: ${({ theme }) => theme.colors.muted};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ButtonPlaceholder = styled.div``;

const FormFooter = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 120px auto 120px;
  grid-column-gap: 0.5rem;
`;

export default MultiPanelForm;
